import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/common/Modal';

const DistributorManagement = () => {
  const { currentUser: user } = useAuth();
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    address: '',
    contact: '',
    email: '',
    password: '',
    city: '',
    region: '',
    phone: '',
    zoneId: '',
  });
  const [zones, setZones] = useState([]);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    // Only directrice_marketing should access this page
    if (user && user.role !== 'directrice_marketing') {
      window.location.href = '/dashboard';
    } else {
      fetchDistributors();
      // Zones terrain, pour rattacher un distributeur à la bonne zone.
      api.zones.getAll()
        .then((res) => setZones(res.data.data || []))
        .catch(() => setZones([]));
    }
  }, [user, searchTerm, showInactive, currentPage, zoneFilter]);

  const fetchDistributors = async () => {
    try {
      setLoading(true);
      const response = await api.distributors.getAll(showInactive, searchTerm, currentPage, itemsPerPage, zoneFilter);
      setDistributors(response.data.data);
      setTotalPages(Math.ceil(response.data.total / itemsPerPage));
      setTotalItems(response.data.total);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching distributors:', error);
      toast.error('Erreur lors du chargement des distributeurs');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleAddDistributor = async (e) => {
    e.preventDefault();
    
    try {
      setFormLoading(true);
      
      await api.distributors.create(formData);
      
      toast.success('Distributeur ajouté avec succès');
      setShowAddModal(false);
      setFormData({
        code: '',
        name: '',
        address: '',
        contact: '',
        email: '',
        password: '',
        city: '',
        region: '',
        phone: '',
        zoneId: '',
      });
      fetchDistributors();
      setFormLoading(false);
    } catch (error) {
      console.error('Error adding distributor:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'ajout du distributeur');
      setFormLoading(false);
    }
  };

  const handleEditDistributor = async (e) => {
    e.preventDefault();
    
    if (!selectedDistributor) return;
    
    try {
      setFormLoading(true);
      
      await api.distributors.update(selectedDistributor._id, formData);
      
      toast.success('Distributeur mis à jour avec succès');
      setShowEditModal(false);
      fetchDistributors();
      setFormLoading(false);
    } catch (error) {
      console.error('Error updating distributor:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la mise à jour du distributeur');
      setFormLoading(false);
    }
  };

  const handleDeleteDistributor = async () => {
    if (!selectedDistributor) return;
    
    try {
      setFormLoading(true);
      
      await api.distributors.deactivate(selectedDistributor._id);
      
      toast.success('Distributeur désactivé avec succès');
      setShowDeleteModal(false);
      fetchDistributors();
      setFormLoading(false);
    } catch (error) {
      console.error('Error deactivating distributor:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la désactivation du distributeur');
      setFormLoading(false);
    }
  };

  const handleImportDistributors = async (e) => {
    e.preventDefault();
    
    if (!importFile) {
      toast.error('Veuillez sélectionner un fichier CSV');
      return;
    }
    
    try {
      setFormLoading(true);
      
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await api.distributors.import(formData);
      
      setImportResults(response.data.data);
      toast.success('Import terminé avec succès');
      fetchDistributors();
      setFormLoading(false);
    } catch (error) {
      console.error('Error importing distributors:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'import des distributeurs');
      setFormLoading(false);
    }
  };

  const openEditModal = (distributor) => {
    setSelectedDistributor(distributor);
    setFormData({
      code: distributor.code,
      name: distributor.name,
      address: distributor.address || '',
      contact: distributor.contact || '',
      email: distributor.accountEmail || distributor.email || '',
      password: '',
      city: distributor.city || '',
      region: distributor.region || '',
      phone: distributor.phone || '',
      zoneId: distributor.zoneId || '',
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (distributor) => {
    setSelectedDistributor(distributor);
    setShowDeleteModal(true);
  };

  const handleFileChange = (e) => {
    setImportFile(e.target.files[0]);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading && distributors.length === 0) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Gestion des distributeurs</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            Importer CSV
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Ajouter un distributeur
          </button>
        </div>
      </div>
      
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Rechercher..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
        <div className="w-full sm:w-56">
          <select
            value={zoneFilter}
            onChange={(e) => { setZoneFilter(e.target.value); setCurrentPage(1); }}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
          >
            <option value="">Toutes les zones</option>
            {zones.map((z) => (
              <option key={z._id} value={z._id}>{z.name}</option>
            ))}
            <option value="none">Sans zone</option>
          </select>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="showInactive"
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            checked={showInactive}
            onChange={() => setShowInactive(!showInactive)}
          />
          <label htmlFor="showInactive" className="ml-2 block text-sm text-gray-900">
            Afficher les distributeurs inactifs
          </label>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zone</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adresse</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {distributors.length > 0 ? (
                distributors.map((distributor) => (
                  <tr key={distributor._id} className={`hover:bg-gray-50 ${!distributor.isActive ? 'bg-gray-100' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{distributor.code}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{distributor.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        {zones.find((z) => String(z._id) === String(distributor.zoneId))?.name || 'Sans zone'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{distributor.address || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{distributor.contact || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {distributor.isActive ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Actif
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Inactif
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openEditModal(distributor)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        Modifier
                      </button>
                      {distributor.isActive && (
                        <button
                          onClick={() => openDeleteModal(distributor)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Désactiver
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                    {searchTerm ? 'Aucun distributeur trouvé pour cette recherche' : 'Aucun distributeur trouvé'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Affichage de <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> à{' '}
                  <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> sur{' '}
                  <span className="font-medium">{totalItems}</span> résultats
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                      currentPage === 1 ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                  >
                    <span className="sr-only">Précédent</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => handlePageChange(i + 1)}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${
                        currentPage === i + 1
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                      currentPage === totalPages ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                  >
                    <span className="sr-only">Suivant</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Add Distributor Modal */}
      {showAddModal && (
        <Modal
          open
          onClose={() => setShowAddModal(false)}
          title="Ajouter un distributeur"
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => setShowAddModal(false)}
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                type="submit"
                form="distributor-add-form"
                className="modal-btn-primary"
                disabled={formLoading}
              >
                {formLoading ? 'Ajout...' : 'Ajouter'}
              </button>
            </>
          }
        >
          <form id="distributor-add-form" onSubmit={handleAddDistributor}>
            <div className="space-y-4">
                        <div>
                          <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                            Code
                          </label>
                          <input
                            type="text"
                            name="code"
                            id="code"
                            value={formData.code}
                            onChange={handleChange}
                            required
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                            Nom
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                            Adresse
                          </label>
                          <input
                            type="text"
                            name="address"
                            id="address"
                            value={formData.address}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact" className="block text-sm font-medium text-gray-700">
                            Contact
                          </label>
                          <input
                            type="text"
                            name="contact"
                            id="contact"
                            value={formData.contact}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                            E-mail du compte de connexion
                          </label>
                          <input
                            type="email"
                            name="email"
                            id="email"
                            value={formData.email}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder="email@distributeur.dz"
                          />
                        </div>
                        <div>
                          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                            Mot de passe du compte
                          </label>
                          <input
                            type="text"
                            name="password"
                            id="password"
                            value={formData.password}
                            onChange={handleChange}
                            autoComplete="off"
                            placeholder="Laisser vide pour ne pas changer"
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                            Téléphone (optionnel)
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            id="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                            Ville (optionnel)
                          </label>
                          <input
                            type="text"
                            name="city"
                            id="city"
                            value={formData.city}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="region" className="block text-sm font-medium text-gray-700">
                            Région / Wilaya (optionnel)
                          </label>
                          <input
                            type="text"
                            name="region"
                            id="region"
                            value={formData.region}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="zoneId" className="block text-sm font-medium text-gray-700">
                            Zone terrain
                          </label>
                          <select
                            name="zoneId"
                            id="zoneId"
                            value={formData.zoneId}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          >
                            <option value="">Aucune zone</option>
                            {zones.map(z => (
                              <option key={z._id} value={z._id}>{z.name}</option>
                            ))}
                          </select>
                          <p className="mt-1 text-xs text-gray-500">
                            Détermine le chef de zone et les animateurs qui reçoivent les demandes de ce distributeur.
                          </p>
                        </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Distributor Modal */}
      {showEditModal && selectedDistributor && (
        <Modal
          open
          onClose={() => setShowEditModal(false)}
          title="Modifier le distributeur"
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => setShowEditModal(false)}
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                type="submit"
                form="distributor-edit-form"
                className="modal-btn-primary"
                disabled={formLoading}
              >
                {formLoading ? 'Mise à jour...' : 'Mettre à jour'}
              </button>
            </>
          }
        >
          <form id="distributor-edit-form" onSubmit={handleEditDistributor}>
            <div className="space-y-4">
                        <div>
                          <label htmlFor="edit-code" className="block text-sm font-medium text-gray-700">
                            Code
                          </label>
                          <input
                            type="text"
                            name="code"
                            id="edit-code"
                            value={formData.code}
                            onChange={handleChange}
                            required
                            disabled
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md bg-gray-100"
                          />
                          <p className="mt-1 text-xs text-gray-500">Le code ne peut pas être modifié</p>
                        </div>
                        <div>
                          <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">
                            Nom
                          </label>
                          <input
                            type="text"
                            name="name"
                            id="edit-name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="edit-address" className="block text-sm font-medium text-gray-700">
                            Adresse
                          </label>
                          <input
                            type="text"
                            name="address"
                            id="edit-address"
                            value={formData.address}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="edit-contact" className="block text-sm font-medium text-gray-700">
                            Contact
                          </label>
                          <input
                            type="text"
                            name="contact"
                            id="edit-contact"
                            value={formData.contact}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700">
                            E-mail du compte de connexion
                          </label>
                          <input
                            type="email"
                            name="email"
                            id="edit-email"
                            value={formData.email}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder="email@distributeur.dz"
                          />
                        </div>
                        <div>
                          <label htmlFor="edit-password" className="block text-sm font-medium text-gray-700">
                            Mot de passe du compte
                          </label>
                          <input
                            type="text"
                            name="password"
                            id="edit-password"
                            value={formData.password}
                            onChange={handleChange}
                            autoComplete="off"
                            placeholder="Laisser vide pour ne pas changer"
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="edit-phone" className="block text-sm font-medium text-gray-700">
                            Téléphone (optionnel)
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            id="edit-phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="edit-city" className="block text-sm font-medium text-gray-700">
                            Ville (optionnel)
                          </label>
                          <input
                            type="text"
                            name="city"
                            id="edit-city"
                            value={formData.city}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="edit-region" className="block text-sm font-medium text-gray-700">
                            Région / Wilaya (optionnel)
                          </label>
                          <input
                            type="text"
                            name="region"
                            id="edit-region"
                            value={formData.region}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                        <div>
                          <label htmlFor="edit-zoneId" className="block text-sm font-medium text-gray-700">
                            Zone terrain
                          </label>
                          <select
                            name="zoneId"
                            id="edit-zoneId"
                            value={formData.zoneId}
                            onChange={handleChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          >
                            <option value="">Aucune zone</option>
                            {zones.map(z => (
                              <option key={z._id} value={z._id}>{z.name}</option>
                            ))}
                          </select>
                          <p className="mt-1 text-xs text-gray-500">
                            Détermine le chef de zone et les animateurs qui reçoivent les demandes de ce distributeur.
                          </p>
                        </div>
            </div>
          </form>
        </Modal>
      )}

      {/* Delete Distributor Modal */}
      {showDeleteModal && selectedDistributor && (
        <Modal
          open
          onClose={() => setShowDeleteModal(false)}
          title="Désactiver le distributeur"
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={formLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                className="modal-btn-danger"
                onClick={handleDeleteDistributor}
                disabled={formLoading}
              >
                {formLoading ? 'Désactivation...' : 'Désactiver'}
              </button>
            </>
          }
        >
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <svg className="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <p className="text-sm text-gray-500">
                Êtes-vous sûr de vouloir désactiver le distributeur {selectedDistributor.name} ? Cette action peut être annulée ultérieurement.
              </p>
            </div>
          </div>
        </Modal>
      )}

      {/* Import Distributors Modal */}
      {showImportModal && (
        <Modal
          open
          onClose={() => {
            setShowImportModal(false);
            setImportFile(null);
            setImportResults(null);
          }}
          title="Importer des distributeurs"
          size="md"
          footer={
            <>
              <button
                type="button"
                className="modal-btn-secondary"
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportResults(null);
                }}
                disabled={formLoading}
              >
                Fermer
              </button>
              <button
                type="submit"
                form="distributor-import-form"
                className="modal-btn-primary"
                disabled={formLoading || !importFile}
              >
                {formLoading ? 'Importation...' : 'Importer'}
              </button>
            </>
          }
        >
          <form id="distributor-import-form" onSubmit={handleImportDistributors}>
            <div className="mt-2">
                        <p className="text-sm text-gray-500 mb-4">
                          Sélectionnez un fichier CSV contenant les distributeurs à importer. Colonnes : code, nom, adresse, contact, email, zone, motdepasse. Seul le code est obligatoire : une colonne vide laisse la valeur actuelle. La zone est le nom de la zone terrain (EST, EST 2, CENTRE EST, CENTRE, CENTRE-B, SUD, CENTRE OUEST, OUEST).
                        </p>
                        <div className="mt-4">
                          <label htmlFor="file" className="block text-sm font-medium text-gray-700">
                            Fichier CSV
                          </label>
                          <input
                            type="file"
                            id="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border border-gray-300 rounded-md"
                            disabled={formLoading}
                          />
                        </div>
                        
                        {importResults && (
                          <div className="mt-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Résultats de l'import</h4>
                            <div className="bg-gray-50 p-3 rounded-md">
                              <p className="text-sm text-gray-700">
                                <span className="font-medium">{importResults.success}</span> distributeurs importés avec succès
                              </p>
                              {importResults.errors > 0 && (
                                <p className="text-sm text-red-600 mt-1">
                                  <span className="font-medium">{importResults.errors}</span> erreurs rencontrées
                                </p>
                              )}
                            </div>
                          </div>
                        )}
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default DistributorManagement;
